<script lang="ts">
  import type { Task } from '../lib/types';
  import { nowState, toggleTaskInNow } from '../lib/stores/now';
  import Icon from './Icon.svelte';

  interface Props {
    task: Pick<Task, 'id' | 'status'>;
    /**
     * menu   — a row in a task's ⋯ menu
     * action — a compact button in an action bar
     * switch — an on/off switch with a label (TaskDetailPopover's "Now" property)
     */
    variant?: 'menu' | 'action' | 'switch';
  }

  let { task, variant = 'menu' }: Props = $props();

  let inNow = $derived($nowState.items.some((item) => item.taskId === task.id));
  let canAdd = $derived($nowState.canAdd);
  let disabled = $derived(task.status !== 'todo' || (!inNow && !canAdd));

  let switchLabel = $derived.by(() => {
    if (inNow) return "In today's Now";
    if (task.status !== 'todo') return 'Only open tasks can be added';
    return canAdd ? "Add to today's Now" : 'Now is full';
  });

  async function handleClick(event: MouseEvent) {
    event.stopPropagation();
    if (disabled) return;
    await toggleTaskInNow(task);
  }
</script>

{#if variant === 'switch'}
  <button
    type="button"
    class="now-toggle switch-toggle"
    class:in-now={inNow}
    role="switch"
    aria-checked={inNow}
    {disabled}
    onclick={handleClick}
  >
    <span class="switch" aria-hidden="true"></span>
    <span class="switch-label">{switchLabel}</span>
  </button>
{:else}
  <button
    type="button"
    class="now-toggle"
    class:menu={variant === 'menu'}
    class:action={variant === 'action'}
    class:in-now={inNow}
    {disabled}
    title={inNow ? 'Remove from Now' : canAdd ? 'Add to Now' : 'Now is full'}
    onclick={handleClick}
  >
    <Icon name={inNow ? 'close' : 'now'} size={variant === 'action' ? 12 : 14} />
    {#if variant === 'action'}
      Now
    {:else if inNow}
      Remove from Now
    {:else}
      Add to Now
    {/if}
  </button>
{/if}

<style>
  .now-toggle {
    font: inherit;
    cursor: pointer;
  }

  .now-toggle:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  /* Matches the task menus' .menu-item; hosts at another scale can override the vars. */
  .menu {
    display: flex;
    align-items: center;
    gap: var(--menu-item-gap, 0.375rem);
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: var(--menu-item-padding, 0.375rem 0.5rem);
    font-size: var(--menu-item-font-size, 0.6875rem);
    color: #374151;
    border-radius: var(--menu-item-radius, 0);
  }

  .menu :global(.icon) {
    color: #6b7280;
  }

  .menu:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .menu.in-now {
    color: #6b7280;
  }

  .action {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    border: none;
    background: #fff7ed;
    color: #9a3412;
    border-radius: 0.25rem;
    padding: 0.375rem 0.25rem;
    font-size: 0.625rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .action:hover:not(:disabled) {
    background: #f9fafb;
  }

  .action.in-now {
    background: #f3f4f6;
    color: #4b5563;
  }

  /* ── switch ─────────────────────────────────────────── */
  .switch-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4375rem;
    padding: 0.125rem 0.25rem 0.125rem 0;
    background: none;
    border: none;
    border-radius: 0.3125rem;
  }

  .switch-toggle:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  .switch {
    position: relative;
    width: 1.625rem;
    height: 0.9375rem;
    flex-shrink: 0;
    border-radius: 999px;
    background: #d1d5db;
    transition: background-color 0.15s;
  }

  .switch::after {
    content: '';
    position: absolute;
    top: 0.125rem;
    left: 0.125rem;
    width: 0.6875rem;
    height: 0.6875rem;
    border-radius: 999px;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: transform 0.15s ease-out;
  }

  .switch-toggle:hover:not(:disabled) .switch {
    background: #9ca3af;
  }

  .switch-toggle.in-now .switch {
    background: #ea580c;
  }

  .switch-toggle.in-now:hover:not(:disabled) .switch {
    background: #c2410c;
  }

  .switch-toggle.in-now .switch::after {
    transform: translateX(0.6875rem);
  }

  .switch-label {
    font-size: 0.71875rem;
    color: #6b7280;
  }

  .switch-toggle.in-now .switch-label {
    color: #9a3412;
  }
</style>
